import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { AccessUser } from '../lib/types';

/**
 * Admin user-management page. Mirrors the old `approval.html` template.
 * No protected route wrapper because the function-side checks the admin cookie
 * and returns 401 if not authenticated — we surface a password prompt instead.
 */
export default function ApprovalPage() {
  const [users, setUsers] = useState<AccessUser[] | null>(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [authError, setAuthError] = useState(false);
  const [insertEmail, setInsertEmail] = useState('');
  const [hideSummary, setHideSummary] = useState<Record<number, boolean>>({});
  const [sendEmail, setSendEmail] = useState<Record<number, boolean>>({});

  async function refresh() {
    try {
      const list = await api.listUsers();
      setUsers(list);
      setAuthError(false);
    } catch (err) {
      if ((err as { status?: number }).status === 401) {
        setUsers(null);
        setAuthError(true);
      }
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function adminLogin(e: React.FormEvent) {
    e.preventDefault();
    setAuthError(false);
    try {
      await api.adminAuthenticate(adminPassword);
      setAdminPassword('');
      await refresh();
    } catch {
      setAuthError(true);
    }
  }

  async function insert() {
    if (!insertEmail) return;
    await api.insertUser(insertEmail);
    setInsertEmail('');
    await refresh();
  }

  async function approve(id: number) {
    await api.approve(id, { hide_summary: hideSummary[id], send_email: sendEmail[id] });
    await refresh();
  }
  async function revoke(id: number) {
    await api.revoke(id);
    await refresh();
  }
  async function deleteUser(id: number) {
    if (!window.confirm('Delete this user?')) return;
    await api.deleteUser(id);
    await refresh();
  }

  if (users === null) {
    return (
      <div className="site-page approval-page">
        <div className="container">
          <div className="row">
            <div className="col-sm-6 col-sm-offset-3 text-center" style={{ paddingTop: 80 }}>
              <h2>Admin Login</h2>
              <form onSubmit={adminLogin}>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Admin password"
                  required
                />{' '}
                <button className="btn" type="submit">Login</button>
                {authError && (
                  <div className="invalid-credentials" style={{ marginTop: 10 }}>
                    Invalid admin password.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="site-page approval-page">
      <div className="container">
        <div className="row">
          <div className="col-xs-12 insert-row">
            <strong>Insert User:</strong>{' '}
            <input
              type="text"
              value={insertEmail}
              onChange={(e) => setInsertEmail(e.target.value)}
              placeholder="email@example.com"
            />{' '}
            <button className="btn" onClick={insert}>Insert</button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="row header-row">
          <div className="col-xs-6"><strong>User Email</strong></div>
          <div className="col-xs-1 text-center"><strong>Admin</strong></div>
          <div className="col-xs-1 text-center"><strong>Hide Summary?</strong></div>
          <div className="col-xs-1 text-center"><strong>Send Email?</strong></div>
          <div className="col-xs-3 text-center"><strong>Approve/Deny</strong></div>
        </div>

        {users.map((user) => (
          <div key={user.id} className="row user-row">
            <div className="col-xs-6">
              {user.email}{' '}
              {user.password && (
                <>
                  (<a href={`/access/${user.password}`} target="_blank" rel="noreferrer">{user.password}</a>)
                </>
              )}
            </div>
            <div className="col-xs-1 text-center">{user.admin === 1 ? 'Yes' : ''}</div>
            <div className="col-xs-1 text-center">
              {!user.password && (
                <label>
                  <input
                    type="checkbox"
                    checked={!!hideSummary[user.id]}
                    onChange={(e) =>
                      setHideSummary((prev) => ({ ...prev, [user.id]: e.target.checked }))
                    }
                  />
                </label>
              )}
            </div>
            <div className="col-xs-1 text-center">
              {!user.password && (
                <label>
                  <input
                    type="checkbox"
                    checked={!!sendEmail[user.id]}
                    onChange={(e) =>
                      setSendEmail((prev) => ({ ...prev, [user.id]: e.target.checked }))
                    }
                  />
                </label>
              )}
            </div>
            <div className="col-xs-3 text-center">
              {!user.password && (
                <button className="btn" onClick={() => approve(user.id)}>APPROVE</button>
              )}
              {user.password && (
                <button className="btn" onClick={() => revoke(user.id)}>REVOKE</button>
              )}{' '}
              <button className="btn" onClick={() => deleteUser(user.id)}>DELETE</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
